// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useAuthContext } from '@tls/auth';
import IconExport from 'components/treactui-template/icons/IconExport';
import IconShare from 'components/treactui-template/icons/IconShare';
import IconUnlocked from 'components/treactui-template/icons/IconUnlocked';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import useProjectExportApi from 'hooks/useProjectExportApi';
import { useProjectLockApi } from 'hooks/useProjectLockApi';
import useOpenProjectWithLock from 'hooks/useOpenProjectWithLock';
import { TFunction } from 'i18next';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch } from 'store/hooks';
import { setDeleteId } from 'store/reducers/projectDeleteSlice';
import { setIdToDuplicateFrom } from 'store/reducers/projectSettingsSlice';
import { setShareId } from 'store/reducers/projectShareSlice';
import { fetchProjects } from 'store/reducers/projectsSlice';
import { AppRoutes } from 'utilities/router/AppRoutes';
import { FunctionMenu } from '@tls/treact-ui';
import { IconDelete, IconDuplicate, IconEdit, IconLoading, IconRefresh } from '@tls/treact-ui/icons';

export type Option = {
  label: string;
  icon: React.JSX.Element;
  id: number;
  disabled: boolean;
};

const labelSelector = (t: TFunction) => (o: Option) => t(o.label);
const iconSelector = (o: Option) => o.icon;
const keySelector = (o: Option) => o.id;
const disabledSelector = (o: Option) => o.disabled;

type Props = {
  projectId: string;
  projectName?: string;
  lockedBy?: string;
};

export default function Options({ projectId, projectName, lockedBy }: Readonly<Props>) {
  const { t } = useTranslation();

  const { currentUser } = useAuthContext();

  const dispatch = useAppDispatch();
  const { loading, exportProject, isExporting } = useProjectExportApi(projectId);
  const { loading: loadingUnlock, unlock } = useProjectLockApi();
  const { openProjectWithLock } = useOpenProjectWithLock();

  const navigate = useNavigate();

  const isLockedByUser = lockedBy && lockedBy === currentUser?.userName;
  const options: Option[] = useMemo(() => {
    const result = [
      /*{ icon: <IconHistory width='1.125rem' />, id: 3, label: 'project.actions.history', disabled: true },*/
      { icon: <IconEdit width='1.125rem' />, id: 1, label: 'project.actions.edit', disabled: false },
      { icon: <IconDuplicate width='1.125rem' />, id: 2, label: 'project.actions.duplicate', disabled: false },
      {
        icon: loading ? <IconLoading /> : <IconExport width='1.125rem' />,
        id: 4,
        label: 'project.actions.export',
        disabled: isExporting,
      },
    ];

    if (isLockedByUser) {
      result.push({
        icon: loadingUnlock ? <IconLoading /> : <IconUnlocked width='1.125rem' />,
        id: 7,
        label: 'project.actions.unlock',
        disabled: loadingUnlock,
      });
    }
    result.push(
      { icon: <IconShare width='1.125rem' />, id: 6, label: 'project.actions.share', disabled: false },
      { icon: <IconRefresh width='1.125rem' />, id: 8, label: 'project.actions.resetModel', disabled: false },
      { icon: <IconDelete width='1.125rem' />, id: 5, label: 'project.actions.delete', disabled: false }
    );

    return result;
  }, [isExporting, loading, isLockedByUser, loadingUnlock]);

  const unlockProject = useCallback(async () => {
    const unlocked = await unlock(projectId);
    ToastByStatus(
      unlocked ? 200 : 500,
      t('project.unlock.title', { projectName }),
      t('project.unlock.error', { projectName }),
      t('project.unlock.success', { projectName })
    );

    if (unlocked) {
      dispatch(fetchProjects());
      navigate(AppRoutes.Projects);
    }
  }, [unlock, projectName, projectId, navigate, t, dispatch]);

  const onClick = useCallback(
    (option: Option) => {
      switch (option.id) {
        case 1:
          openProjectWithLock(projectId, 'editProject');
          break;
        case 2:
          dispatch(setIdToDuplicateFrom(projectId));
          break;
        case 4:
          exportProject();
          break;
        case 5:
          dispatch(setDeleteId(projectId));
          break;
        case 6:
          dispatch(setShareId(projectId));
          break;
        case 7:
          unlockProject();
          break;
        case 8:
          openProjectWithLock(projectId, 'resetModel');
          break;
      }
    },
    [dispatch, openProjectWithLock, exportProject, projectId, unlockProject]
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <span onClick={e => e.stopPropagation()}>
      <FunctionMenu
        className='moveRight'
        options={options}
        labelSelector={labelSelector(t)}
        iconSelector={iconSelector}
        keySelector={keySelector}
        disabledSelector={disabledSelector}
        onClick={onClick}
        noBorder
        noShadow
        style={transparent}
        data-testid={`project-${projectId}-action-menu`}
        dataTestIdSelector={option => `project-${projectId}-action-${option.id}-button`}
      />
    </span>
  );
}

const transparent: React.CSSProperties = {
  backgroundColor: 'transparent',
};

export { transparent, labelSelector, iconSelector, disabledSelector, keySelector };
